import ContentError from 'components/ContentError'
import ContentLoading from 'components/ContentLoading'
import SearchField from 'components/SearchField'
import {
  SideSheetElevation,
  SideSheetHostView,
  SideSheetMain,
  useSideSheet,
} from 'components/SideSheet'
import {
  StickyViewHeader,
  StickyViewHeaderExtras,
  StickyViewHeaderMain,
  StickyViewHeaderSpacer,
} from 'components/StickyViewHeader'
import { useDocumentTitle } from 'hooks/document-title'
import { useOnEscDown } from 'hooks/key-press'
import { useActivation } from 'hooks/mobx-hooks'
import { useScrollOffsetSpring } from 'hooks/spring-hooks'
import JobsViewHeading from 'jobs/components/JobsViewHeading'
import ResultCounts from 'jobs/components/ResultCounts'
import { observer } from 'mobx-react'
import { SearchState } from 'opportunities/OpportunitiesSearchViewModel'
import OpportunitiesViewModel from 'opportunities/OpportunitiesViewModel'
import * as React from 'react'
import { RouteChildrenProps } from 'react-router'
import OpportunityDetailsSheet from '../OpportunityDetailsSheet'
import OpportunitiesList from './OpportunitiesList'
import styles from './OpportunitiesView.sass'
import OverachieverModal from '../OverachieverModal'
import OverachieverBanner from '../OverachieverBanner'
import PtinRequiredView from '../../../components/PtinRequiredView'
import OpportunitiesRootViewModel, {
  OpportunitiesTabs,
} from '../../OpportunitiesRootViewModel'
import { match as patternMatch } from '../../../utils/pattern-match-util'
import WorkspaceOpportunities from '../../workspace-postings-opportunities/views/WorkspaceOpportunities'
import { ContentMain, ContentLayout } from '../../../components/ContentLayout'
import OpportunitiesTabBar from '../../components/OpportunitiesTabBar'

interface OpportunitiesViewProps
  extends RouteChildrenProps<{ opportunityId?: string }> {
  vm: OpportunitiesRootViewModel
}

interface OpportunitiesMainViewProps
  extends React.HTMLAttributes<HTMLDivElement> {
  vm: OpportunitiesViewModel
  tabBars?: React.ReactNode
}

export default observer(function OpportunitiesViewGatekeeper({
  vm,
  match,
}: OpportunitiesViewProps) {
  const opportunityId = match?.params?.opportunityId

  useDocumentTitle('Opportunities')
  useActivation(vm)
  React.useEffect(() => {
    if (vm.canViewOps.resolved && opportunityId) {
      vm.opportunitiesViewModel.viewOpportunity(opportunityId)
    }
  }, [opportunityId, vm.canViewOps.resolved])

  return vm.canViewOps.match({
    pending: () => <ContentLoading />,
    rejected: (err) => <ContentError error={err} />,
    resolved: () =>
      patternMatch(vm.opportunitiesTabController.current.tab, {
        [OpportunitiesTabs.Jobs]: () => {
          return (
            <OpportunitiesView
              vm={vm.opportunitiesViewModel}
              tabBars={
                vm.showTabs && (
                  <OpportunitiesTabBar
                    tabsController={vm.opportunitiesTabController}
                  />
                )
              }
            />
          )
        },
        [OpportunitiesTabs.Workspaces]: () =>
          vm.workspaceOpportunitiesViewModel && (
            <WorkspaceOpportunities
              vm={vm.workspaceOpportunitiesViewModel}
              tabBars={
                vm.showTabs && (
                  <OpportunitiesTabBar
                    tabsController={vm.opportunitiesTabController}
                  />
                )
              }
            />
          ),
      }),
  })

  // <AccessDeniedView
  //   title="You are not allowed to view Opportunities"
  //   description="If you think this is a mistake, please contact your administrator."
  // />
})

const OpportunitiesView = observer(function OpportunitiesView({
  vm,
  tabBars,
}: OpportunitiesMainViewProps) {
  useActivation(vm)
  const searchState = vm.searcher.searchState
  if (searchState === SearchState.InitialSearchRunning) {
    return <ContentLoading />
  }

  return <OpportunitiesMainView vm={vm} tabBars={tabBars} />
})

const OpportunitiesMainView = observer(function OpportunitiesMainView({
  vm,
  tabBars,
}: OpportunitiesMainViewProps) {
  const sideSheet = useSideSheet()
  const scrollRef = React.useRef<HTMLDivElement | null>(null)
  const scrollOffset = useScrollOffsetSpring(scrollRef)
  useOnEscDown(vm.searcher.clearSearchText)

  if (vm.ptinRequired) {
    return <PtinRequiredView />
  }

  return (
    <>
      <SideSheetHostView
        elevation={sideSheet.elevation}
        sheetVisible={!!vm.detailsVM.selected || !sideSheet.shouldBeAbove}
        onRequestHide={vm.detailsVM.deselect}
      >
        <SideSheetMain>
          <div className={styles.root} ref={scrollRef}>
            <StickyViewHeader scrollOffset={scrollOffset} tabBar={tabBars}>
              <StickyViewHeaderMain>
                <JobsViewHeading
                  searcher={vm.searcher}
                  viewTitle="Opportunities"
                  allOptionText="All Opportunities"
                  workspaces={vm.workspacesWhereUserCanViewOpportunities}
                />
              </StickyViewHeaderMain>
              <StickyViewHeaderSpacer />
              <StickyViewHeaderExtras>
                <SearchField
                  onChange={vm.searcher.setSearchText}
                  value={vm.searcher.searchText}
                  dataTest="opportunity-search-field"
                />
              </StickyViewHeaderExtras>
            </StickyViewHeader>
            <ContentLayout accountForTabBar={Boolean(tabBars)}>
              <ContentMain>
                {(!vm.canPickUp ||
                  vm.workspacesWithoutPickUpCapacity.length > 0) && (
                  <OverachieverBanner vm={vm} />
                )}

                <div className={styles.resultsCount}>
                  <ResultCounts
                    showing={vm.opportunities.length}
                    // Don't show total because it will be wrong due to
                    // how Work API filters Ops.
                    total={null}
                  />
                </div>
                <OpportunitiesList
                  vm={vm}
                  showWorkspaceInfo={
                    vm.searcher.workspaceFilter.type === 'AllWorkspaces' &&
                    vm.workspacesWhereUserCanViewOpportunities.length > 1
                  }
                />
              </ContentMain>
            </ContentLayout>
          </div>
        </SideSheetMain>

        {vm.canPickUp && (
          <OpportunityDetailsSheet
            vm={vm.detailsVM}
            // Only show the close button when in overlay mode
            onCloseClick={
              sideSheet.elevation === SideSheetElevation.Above
                ? vm.detailsVM.deselect
                : undefined
            }
          />
        )}
      </SideSheetHostView>
      <OverachieverModal vm={vm} />
    </>
  )
})
